import { Spacing } from '@persona-id/design-tokens';
import {
  ColumnDef,
  ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { uniq } from 'lodash/fp';
import { useState } from 'react';
import styled from 'styled-components';

import LayoutTableColumnConfigurationPopover from 'dashboard/modules/Resource/Layouts/LayoutTableColumnConfigurationPopover';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: ${Spacing[2]};
`;

const TableBodyZeroState = styled('div')`
  margin-top: ${Spacing[2]};
  padding: ${Spacing[7]} ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  text-align: center;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
`;

const TableWrapper = styled('div')`
  overflow: auto;
`;

export interface LayoutTableProps<ColumnDefType> {
  cardTitle: string;
  nullStateText: string;
  defaultVisibleKeys: string[];
  columnIdToLabel: Record<string, string>;
  columnDefs: ColumnDef<ColumnDefType, any>[];
  tableData: ColumnDefType[];
}

// Note that the dangling ',' is required so that the typescript compiler correctly compiles this as a generic type.
// See https://stackoverflow.com/a/66052574 for further explanation.
const LayoutTable = <ColumnDefType,>({
  cardTitle,
  nullStateText,
  defaultVisibleKeys,
  columnIdToLabel,
  columnDefs,
  tableData,
}: LayoutTableProps<ColumnDefType>) => {
  const [columns] = useState(columnDefs);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    // NOTE(brandon): we want to order by defaultVisibleKeys first, then the rest of the columns. uniq is used to remove duplicates while preserving order.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    uniq(['select', ...defaultVisibleKeys, ...columns.map((column) => column.id)]),
  );

  const handleSetColumnOrder = (columnOrder: ColumnOrderState) => {
    // Prevent reordering the checkbox column.
    setColumnOrder(['select', ...columnOrder]);
  };

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    // Make sure the checkbox column is visible on render.
    const initialVisibleKeys = ['select', ...defaultVisibleKeys];
    return columns.reduce((accum: Record<string, boolean>, curr) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accum[curr.id] = initialVisibleKeys.includes(curr.id);
      return accum;
    }, {});
  });

  const toggleColumnVisibilityById = (id: string) => {
    const column = transactionsTable.getColumn(id);
    column?.toggleVisibility();
  };

  const transactionsTable = useReactTable({
    data: tableData,
    columns: columns,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const headers = transactionsTable.getLeafHeaders();
  const rows = transactionsTable.getRowModel().rows;

  return (
    <Card>
      <Card.Body>
        <TableTitleContainer>
          <Card.Title>{cardTitle}</Card.Title>
          <HStack spacing={Spacing[2]}>
            <LayoutTableColumnConfigurationPopover
              columnOrder={columnOrder.filter((c) => c !== 'select')}
              columnVisibilityState={transactionsTable.getState().columnVisibility}
              columnIdToLabel={columnIdToLabel}
              updateColumnOrder={handleSetColumnOrder}
              updateColumnVisibility={toggleColumnVisibilityById}
            />
          </HStack>
        </TableTitleContainer>
        <TableWrapper>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeaderCell
                    id={header.id}
                    key={header.id}
                    sortable={header.column.getCanSort()}
                    onClick={header.column.getToggleSortingHandler()}
                    sortDirection={header.column.getIsSorted()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
        {rows.length === 0 && <TableBodyZeroState>{nullStateText}</TableBodyZeroState>}
      </Card.Body>
    </Card>
  );
};

export default LayoutTable;
